import { useEffect, useRef, useState } from 'react';
import { ArrowsInSimpleIcon, ArrowsOutSimpleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/Tooltip';

interface ExpandableDetailsCellProps {
    text: string;
    isExpanded: boolean;
    onToggle: () => void;
    expandLabel: string;
    collapseLabel: string;
}

export function ExpandableDetailsCell({
    text,
    isExpanded,
    onToggle,
    expandLabel,
    collapseLabel,
}: ExpandableDetailsCellProps) {
    const codeElementRef = useRef<HTMLElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const currentCodeElement = codeElementRef.current;
        if (!currentCodeElement) return;
        const measure = () =>
            setIsTruncated(
                currentCodeElement.scrollWidth > currentCodeElement.clientWidth,
            );
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(currentCodeElement);
        return () => observer.disconnect();
    }, [text]);

    const showButton = isExpanded || isTruncated;
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const tooltipAllowed = isTruncated && !isExpanded;

    return (
        <div className="group/details flex items-center gap-2 min-w-0">
            <Tooltip
                open={tooltipAllowed && tooltipOpen}
                onOpenChange={setTooltipOpen}
            >
                <TooltipTrigger asChild>
                    <code
                        ref={codeElementRef}
                        className="text-xs text-grey-300 font-geist-mono truncate flex-1 min-w-0"
                    >
                        {text}
                    </code>
                </TooltipTrigger>
                <TooltipContent
                    side="top"
                    sideOffset={8}
                    className="max-w-[480px] font-geist-mono break-all whitespace-pre-wrap"
                >
                    {text}
                </TooltipContent>
            </Tooltip>
            {showButton && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggle();
                    }}
                    aria-label={isExpanded ? collapseLabel : expandLabel}
                    aria-expanded={isExpanded}
                    className={cn(
                        'shrink-0 flex items-center justify-center size-6 rounded text-grey-300 hover:text-white-100 hover:bg-surface-hover transition-opacity duration-150',
                        isExpanded
                            ? 'opacity-100'
                            : 'opacity-0 group-hover/details:opacity-100 focus-visible:opacity-100',
                    )}
                >
                    <span className="relative size-4 -rotate-45">
                        <ArrowsOutSimpleIcon
                            className={cn(
                                'absolute inset-0 size-4 transition-all duration-200 ease-out',
                                isExpanded
                                    ? 'opacity-0 scale-75 rotate-45'
                                    : 'opacity-100 scale-100 rotate-0',
                            )}
                            weight="bold"
                        />
                        <ArrowsInSimpleIcon
                            className={cn(
                                'absolute inset-0 size-4 transition-all duration-200 ease-out',
                                isExpanded
                                    ? 'opacity-100 scale-100 rotate-0'
                                    : 'opacity-0 scale-75 -rotate-45',
                            )}
                            weight="bold"
                        />
                    </span>
                </button>
            )}
        </div>
    );
}
